import { useMemo, useState } from "react";
import { Elements, useElements } from "@stripe/react-stripe-js";
import { Stripe } from "@stripe/stripe-js";
import styled from "@emotion/styled";
import { boolean, object, string } from "yup";
import {
  BusinessPlanOptions,
  FamilyPlanOptions,
  getCountryByCountryCode,
  toCurrency,
  projectLocationAddonMapper,
  Location,
  Country,
  ReferralSourceEnum,
  putCommasIntoNumber,
  CAPTCHA_ACTIONS,
} from "@ecologi/shared";
import { Select } from "../Select";
import { Button } from "../Button";
import { Text } from "../Text";
import { TextField } from "../TextField";
import { useRecaptcha } from "../Recaptcha";
import {
  FormLayout,
  FormItem,
  RowItems,
  RowItem,
} from "../FormLayout/FormLayout.styles";
import { Typography } from "../Typography";
import { InlineBanner } from "../InlineBanner";
import { selectPlanData } from "./selectors";
import { TermsAcceptance } from "../TermsAcceptance";
import { useForm } from "../utils/use-form";
import { locationCodeLabelMapper, referralSourceOptions } from "./fields";
import styles from "./styles/CheckoutForm.module.scss";
import {
  StripeCardNumber,
  StripeCardExpiry,
  StripeCardCVC,
} from "../StripeElements";
import { OnSubmitCheckoutFormProps } from "./utils/on-submit-checkout-form";
import { FormFooter } from "../FormFooter";

const CheckoutFAQ = styled.div`
  margin-top: 30px;
`;

const FormFooterContainer = styled.div`
  margin-top: 3rem;
  margin-bottom: 1.2rem;
`;

export type CheckoutFormBaseProps = {
  numLocalTrees: number;
  selectedPlanAddons?: FamilyPlanOptions | BusinessPlanOptions;
  selectedPlanId: string | null;
};

export type CheckoutFormProps = {
  errorMessage: string;
  isLoading: boolean;
  isProduction?: boolean;
  location: Location | null;
  onSubmit: (args: OnSubmitCheckoutFormProps) => Promise<any>;
  stripe: Stripe | null;
  subscribeUser: any;
} & CheckoutFormBaseProps;

export type CheckoutFormFields = {
  country: Country;
  emailAddress: string;
  firstName: string;
  groupName?: string;
  isMarketingAccepted: boolean;
  isTermsAndConditionsAccepted: boolean;
  lastName: string;
  password: string;
  postCode: string;
  referralSource: string;
  testClockId?: string;
};

const getSchema = (hasGroupName: boolean) =>
  object().shape({
    emailAddress: string()
      .default("")
      .email("Please use a valid email address")
      .required("Email address is a required field"),
    password: string()
      .default("")
      .min(8, "Please use 8 or more characters")
      .required("Password is a required field"),
    ...(hasGroupName
      ? {
          groupName: string()
            .default("")
            .max(50, "Please use less than 50 characters")
            .required(),
        }
      : {}),
    firstName: string().default("").required(),
    lastName: string().default("").required(),
    postCode: string().default("").required(),
    referralSource: string().default(ReferralSourceEnum.UNKNOWN).optional(),
    isTermsAndConditionsAccepted: boolean().default(false).isTrue(),
    isMarketingAccepted: boolean().default(false).isTrue(),
    testClockId: string().optional(),
  });

export const DumbCheckoutForm: React.FC<
  CheckoutFormProps & { selectedPlanId: string }
> = ({
  errorMessage,
  isLoading,
  isProduction,
  location,
  numLocalTrees,
  onSubmit,
  selectedPlanAddons,
  selectedPlanId,
  subscribeUser,
}) => {
  const { selectedPlan, currencySymbol, isFamilyPlan, isBusinessPlan } =
    useMemo(
      () => selectPlanData(location, selectedPlanId, selectedPlanAddons),
      [location, selectedPlanId, selectedPlanAddons]
    );

  const elements = useElements();

  const recaptcha = useRecaptcha();

  const [isCardNumberValid, setCardNumberIsValid] = useState(false);
  const [isCardCVCValid, setCardCVCIsValid] = useState(false);
  const [isCardExpiryValid, setCardExpiryIsValid] = useState(false);

  const countryCode = location
    ? location.countryCode !== "EU"
      ? location.countryCode
      : "GB"
    : "US";

  const schema = useMemo(
    () => getSchema(isFamilyPlan || isBusinessPlan),
    [isFamilyPlan, isBusinessPlan]
  );

  const form = useForm<Omit<CheckoutFormFields, "country">>({
    validationSchema: schema,
    initialValues: schema.getDefaultFromShape(),
    onSubmit: async (formValues) => {
      const captchaToken = await recaptcha.execute(
        isBusinessPlan
          ? CAPTCHA_ACTIONS.BUSINESS_SUB
          : CAPTCHA_ACTIONS.INDIVIDUAL_SUB
      );
      onSubmit({
        elements,
        formValues: { ...formValues, country: countryCode },
        isBusinessPlan,
        isCardCVCValid,
        isCardExpiryValid,
        isCardNumberValid,
        isFamilyPlan,
        numLocalTrees,
        selectedPlan,
        selectedPlanAddons,
        selectedPlanId,
        subscribeUser,
        captchaToken,
      });
    },
  });

  if (!selectedPlan) {
    return null;
  }

  const localProjectAddon = location
    ? projectLocationAddonMapper[location.countryCode]
    : null;

  const totalCost =
    (localProjectAddon?.subscriptionPrice || 0) * numLocalTrees +
    selectedPlan.price;

  const totalCostString = `${currencySymbol}${toCurrency(totalCost)}`;

  let confirmText = `You'll pay ${putCommasIntoNumber(
    totalCostString
  )} per month for your `;
  if (isBusinessPlan) {
    confirmText = `${confirmText} custom business plan.`;
  } else if (isFamilyPlan) {
    confirmText = `${confirmText} custom family plan.`;
  } else {
    confirmText = `${confirmText} ${selectedPlan.title.toLowerCase()} plan.`;
  }

  const locationCodeLabel =
    location?.countryCode == "US"
      ? locationCodeLabelMapper.US
      : locationCodeLabelMapper.default;

  return (
    <>
      <Text
        isCenter
        isHeading
        style={{ marginBottom: "32px" }}
        text="Set up your account"
      />
      <form
        id="checkout-form"
        className={isLoading ? styles.formDisabled : ""}
        onSubmit={form.handleSubmit}
        data-testid="checkout-form"
      >
        <FormLayout>
          <RowItems>
            <RowItem>
              <TextField
                {...form.getFieldProps("emailAddress", "Email address")}
                type="email"
                errorText={form.errors.emailAddress?.toString()}
              />
            </RowItem>
            <RowItem>
              <TextField
                {...form.getFieldProps("password", "Password")}
                type="password"
                errorText={form.errors.password?.toString()}
              />
            </RowItem>
          </RowItems>
          <Text
            isCenter
            isSubheading
            style={{
              borderTop: "2px solid #f2f2f2",
              fontWeight: "bold",
              marginBottom: "20px",
              marginTop: "50px",
              paddingTop: "32px",
            }}
            text="Next, we need a few details"
          />
          {(isFamilyPlan || isBusinessPlan) && (
            <RowItems>
              <FormItem>
                <TextField
                  {...form.getFieldProps(
                    "groupName",
                    isFamilyPlan ? "Family name" : "Business name"
                  )}
                  maxLength={50}
                  showCharacterCount
                  placeholder={
                    isFamilyPlan
                      ? "Example 'The Jacks'"
                      : "Example 'Lucy's Shoes'"
                  }
                  helpText="This will be displayed on your profile"
                />
              </FormItem>
            </RowItems>
          )}
          <RowItems>
            <RowItem>
              <TextField {...form.getFieldProps("firstName", "First name")} />
            </RowItem>
            <RowItem>
              <TextField {...form.getFieldProps("lastName", "Last name")} />
            </RowItem>
          </RowItems>
          <RowItems>
            <RowItem>
              <TextField
                {...form.getFieldProps("postCode", locationCodeLabel)}
              />
            </RowItem>
            <RowItem>
              <TextField
                label="Country"
                name="country"
                value={getCountryByCountryCode(countryCode)}
                isDisabled={true}
                helpText="You can update your country at the bottom of this page"
              />
            </RowItem>
          </RowItems>
          <RowItem>
            <StripeCardNumber
              onChange={(e) => setCardNumberIsValid(e.complete)}
              isDisabled={isLoading}
              label="Enter your card details"
            />
          </RowItem>
          <RowItems>
            <RowItem>
              <StripeCardExpiry
                onChange={(e) => setCardExpiryIsValid(e.complete)}
                isDisabled={isLoading}
                label="Expiry date"
              />
            </RowItem>
            <RowItem>
              <StripeCardCVC
                onChange={(e) => setCardCVCIsValid(e.complete)}
                isDisabled={isLoading}
                label="CVC"
              />
            </RowItem>
          </RowItems>
        </FormLayout>
        {errorMessage && (
          <div
            style={{ position: "relative", top: isBusinessPlan ? "15px" : "0" }}
          >
            <InlineBanner title="There was an error!" status="critical">
              <p>{errorMessage}</p>
            </InlineBanner>
            <br />
          </div>
        )}
        <p className={styles.checkoutConfirmText}>{confirmText}</p>
        <div className={styles.referralSourceGroup}>
          <Select
            {...form.getFieldProps(
              "referralSource",
              "Where did you hear about Ecologi?"
            )}
            data-testid="referral-source-select"
            items={referralSourceOptions}
            id="referralSource"
          />
        </div>
        <TermsAcceptance
          isLoading={isLoading}
          isTermsChecked={form.values["isTermsAndConditionsAccepted"]}
          isEmailTermsChecked={form.values["isMarketingAccepted"]}
          disableEmailCheckbox
          setIsTermsChecked={(isChecked) => {
            form.handleChange({
              target: {
                name: "isTermsAndConditionsAccepted",
                value: isChecked,
              },
            });
          }}
          setIsEmailTermsChecked={(isChecked) =>
            form.handleChange({
              target: {
                name: "isMarketingAccepted",
                value: isChecked,
              },
            })
          }
        />

        {!isProduction && (
          <div style={{ marginBottom: "20px" }}>
            <TextField
              {...form.getFieldProps("testClockId", "Test Clock ID")}
              helpText="Enter a Stripe test clock ID to attach this user to a test clock"
            />
          </div>
        )}

        <Button
          disabled={!form.isValid || form.isSubmitting}
          extra={{ style: { margin: "0 auto" } }}
          loading={isLoading || form.isSubmitting}
          primary
          submit
          text="Subscribe and pay"
        />
        <FormFooterContainer>
          <FormFooter showRecaptchaPolicy />
        </FormFooterContainer>
        <CheckoutFAQ>
          <Typography as="h3" use="body2">
            Can I change or cancel my plan?
          </Typography>
          <Typography as="p" use="body1">
            Yes, you can change your plan at any time. If you'd like to cancel
            your subscription or switch to a new plan, you can do easily when
            editing your profile or messaging us directly.
          </Typography>
        </CheckoutFAQ>
        <CheckoutFAQ>
          <Typography as="h3" use="body2">
            Powered by Stripe
          </Typography>
          <Typography as="p" use="body1">
            This page uses Stripe's secure checkout. By using it you're
            accepting their{" "}
            <Typography
              as="a"
              href="https://stripe.com/checkout/terms"
              rel="noopener noreferrer"
              target="_blank"
              title="Review Stripe's Ts&amp;Cs"
              use="inlineLink"
            >
              Terms &amp; Conditions
            </Typography>
            {" and "}
            <Typography
              as="a"
              href="https://stripe.com/privacy"
              rel="noopener noreferrer"
              target="_blank"
              title="Review Stripe's Privacy Policy"
              use="inlineLink"
            >
              Privacy Policy
            </Typography>
            .
          </Typography>
        </CheckoutFAQ>
      </form>
    </>
  );
};

const withEmptyPlanIdWrapper =
  (Component: React.ComponentType<CheckoutFormProps>) =>
  (props: CheckoutFormProps) => {
    const { stripe } = props;
    if (!props.selectedPlanId) {
      return (
        <div>
          An error has occurred. Please refresh the page and try again
          <br />
          <br />
          <Button
            text="Refresh"
            onClick={(e) => {
              e.preventDefault();
              window.location.reload();
            }}
          />
        </div>
      );
    }

    return (
      <Elements stripe={stripe}>
        <Component {...props} />
      </Elements>
    );
  };

export const CheckoutForm = withEmptyPlanIdWrapper(DumbCheckoutForm);
